/**
 * MonEcheancierSEPA — Relevé de contribution du membre.
 * Affiche, par souscription RCDS échelonnée, le mandat SEPA et ses prélèvements
 * (numéro, date, montant, statut). Lecture seule.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { CalendarClock, CheckCircle2, Clock, XCircle, Landmark, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const euros = (cents) => ((cents || 0) / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso; }
};

const STATUS = {
  scheduled: { label: 'Programmé', cls: 'bg-amber-100 text-amber-700', Icon: Clock },
  pending: { label: 'En cours', cls: 'bg-blue-100 text-blue-700', Icon: Clock },
  paid: { label: 'Payé', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  confirmed: { label: 'Payé', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  failed: { label: 'Échec', cls: 'bg-red-100 text-red-700', Icon: XCircle },
};

const MANDAT = {
  active: { label: 'Mandat actif', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Mandat en attente', cls: 'bg-amber-100 text-amber-700' },
  pending_submission: { label: 'Mandat en attente', cls: 'bg-amber-100 text-amber-700' },
  failed: { label: 'Mandat échoué', cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Mandat annulé', cls: 'bg-slate-100 text-slate-600' },
};

export const MonEcheancierSEPA = ({ getAuthHeader }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/souscriptions-rcd/me/echeanciers`, { headers: getAuthHeader() });
        setItems(res.data?.echeanciers || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeader]);

  const downloadPdf = async (reference) => {
    try {
      const res = await axios.get(
        `${API}/souscriptions-rcd/me/echeanciers/${encodeURIComponent(reference)}/pdf`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `releve-sepa-${reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      /* noop */
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <Card className="border-l-4 border-l-[#DEA900]" data-testid="mon-echeancier-sepa">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-[#DEA900]" />
          Mon relevé de contribution SEPA
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {items.map((it, idx) => {
          const p = it.plan || {};
          const s = it.souscription || {};
          const mandat = MANDAT[p.mandate_status] || { label: p.mandate_status || 'Mandat', cls: 'bg-slate-100 text-slate-600' };
          return (
            <div key={idx} className="rounded-lg border border-slate-200 p-3" data-testid={`echeancier-${s.reference}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                    <Landmark className="h-4 w-4 text-slate-400" />
                    {s.reference}
                  </p>
                  <p className="text-xs text-slate-500">
                    Total échelonné : {euros(p.montant_total_cents)} · {(p.installments || []).length} prélèvements
                  </p>
                </div>
                <Badge className={`${mandat.cls} shrink-0`} data-testid={`mandat-status-${s.reference}`}>{mandat.label}</Badge>
              </div>
              <button
                type="button"
                onClick={() => downloadPdf(s.reference)}
                className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#0a3d62] hover:text-[#DEA900] transition-colors"
                data-testid={`download-releve-${s.reference}`}
              >
                <Download className="h-3.5 w-3.5" /> Télécharger le relevé (PDF)
              </button>
              <div className="divide-y divide-slate-100">
                {(p.installments || []).map((e) => {
                  const st = STATUS[e.status] || { label: e.status, cls: 'bg-slate-100 text-slate-600', Icon: Clock };
                  const StIcon = st.Icon;
                  return (
                    <div key={e.index} className="flex items-center justify-between py-2 text-sm" data-testid={`installment-${s.reference}-${e.index}`}>
                      <span className="text-slate-500 w-10">#{e.index}</span>
                      <span className="flex-1 text-slate-700">{fmtDate(e.charge_date)}</span>
                      <span className="font-medium text-slate-900 w-24 text-right">{euros(e.amount_cents)}</span>
                      <span className="w-28 flex justify-end">
                        <Badge className={`${st.cls} gap-1`}><StIcon className="h-3 w-3" />{st.label}</Badge>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default MonEcheancierSEPA;
